<template>
  <div class="container-mobile animated fadeIn" style="top: 95px">
    <div class="content-contact-mobile content-document-mobile form">
      <b-row>
        <b-col style="display: flex">
          <div class="form-input-search">
            <b-form-input v-model="filter" type="text" :placeholder="FormMSG(1, 'Type to search')" class="search" @input="handleInputFilter"></b-form-input>
          </div>
          <div class="form-input-icon-search" style="cursor: pointer" @click="clearFilter">
            <search v-if="filter === ''" color="#06263E" :size="16" />
            <x v-else color="#06263E" :size="16" />
          </div>
        </b-col>
      </b-row>
      <b-row style="margin-top: 16px">
        <b-col>
          <b-form-select v-model="docTypeSelected" class="mb-3" @change="handleChangeDocType">
            <b-form-select-option :value="null">{{ FormMSG(4, 'All') }}</b-form-select-option>
            <b-form-select-option v-for="(docType, index) in docTypes" :key="index" :value="docType.value">
              {{ docType.text }}
            </b-form-select-option>
          </b-form-select>
        </b-col>
      </b-row>
      <b-row style="margin-top: 16px">
        <b-col>
          <div class="title-list">
            <h2>{{ FormMSG(2, 'Files') }}</h2>
          </div>
        </b-col>
      </b-row>
      <div v-if="docs.length === 0" class="text-center">
        <p>{{ FormMSG(3, 'No result') }}</p>
      </div>
      <div v-for="(doc, index) in docs" :key="index">
        <div v-if="index > 0" class="divider-line mt-3 mb-2" />
        <b-row :class="{ 'mt-3': index === 0 }">
          <b-col cols="10" class="pr-0 d-flex flex-column justify-content-start">
            <p class="name-contact mt-1 mb-0">
              <label>
                {{ doc.name }}
              </label>
            </p>
            <p v-if="[DOCUMENTS_TYPE.CONTRACT_TEMPLATE, DOCUMENTS_TYPE.ON_BOARDING_PACKAGE, DOCUMENTS_TYPE.BOX_KIT].includes(doc.type)" class="mb-1">
              {{ dateToString(doc.deliveryContractStrDate) }} -
              {{ dateToString(doc.deliveryContractEndDate) }}
            </p>
            <p class="mt-2 mb-0">
              <span class="status info">
                {{ getDocType(doc.type) }}
              </span>
              <span class="divider-dot" />
              {{ getPublishedOn(doc.publishedOn) }}
            </p>
            <div>
              <Button v-if="doc.comments && doc.comments !== ''" v-b-tooltip.right="doc.comments" class="mt-3 mb-0 p-0 p-button-text">
                <span class="wrap-status d-flex justify-content-start">
                  <span :class="`status refused`" style="font-size: 0.7rem">
                    {{ validatedStatus(doc) }}
                  </span>
                </span>
              </Button>
              <Button v-else class="mt-3 mb-0 p-0 p-button-text">
                <span class="wrap-status d-flex justify-content-start">
                  <span :class="`status ${doc.validatedClass}`" style="font-size: 0.7rem">
                    {{ validatedStatus(doc) }}
                  </span>
                </span>
              </Button>
            </div>
          </b-col>
          <b-col cols="2" class="d-flex align-items-start">
            <b-dropdown variant="light" toggle-class="transparent" no-caret dropleft>
              <template #button-content>
                <MoreVertical color="#06263E" :size="20" />
              </template>
              <b-dropdown-item @click="viewDocument(doc)">
                <div class="d-flex justify-content-start align-items-center w-100">
                  <component :is="getLucideIcon('Eye')" color="#06263E" :size="16" />
                  <div class="pl-2">
                    {{ FormMSG(7, 'View document') }}
                  </div>
                </div>
              </b-dropdown-item>
              <b-dropdown-item @click="viewDocumentDetail(doc)">
                <div class="d-flex justify-content-start align-items-center w-100">
                  <component :is="getLucideIcon('Edit2')" color="#06263E" :size="16" />
                  <div class="pl-2">
                    {{ FormMSG(5, 'See Details') }}
                  </div>
                </div>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="doc.status === DOCUMENT_DELIVERY_STATUS.READ && doc.requireManagerApproval === true && doc.toSign === true"
                @click="handleClickSign(doc)"
              >
                <div class="d-flex justify-content-start align-items-center w-100">
                  <component :is="getLucideIcon('FileSignature')" color="#06263E" :size="16" />
                  <div class="pl-2">
                    {{ FormMSG(15, 'Sign') }}
                  </div>
                </div>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="
                  doc.deliveryCurrentStep > 1 &&
                  ![DOCUMENT_DELIVERY_STATUS.REFUSED, DOCUMENT_DELIVERY_STATUS.SIGNED, DOCUMENT_DELIVERY_STATUS.VALIDATED].includes(doc.status)
                "
                @click="handleClickRefuse(doc)"
              >
                <div class="d-flex justify-content-start align-items-center w-100">
                  <component :is="getLucideIcon('X')" :color="ICONS.TRASH.color" :size="18" :stroke-width="3" />
                  <div class="pl-2" style="color: #ea4e2c">
                    {{ FormMSG(5744567856, 'Refuse') }}
                  </div>
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
        </b-row>
      </div>
      <dialog-for-refuse-document
        :open="showDialogRefuseDocument"
        :document-selected="documentSelected ? documentSelected : {}"
        @dialog-for-refuse-document:success="handleDialogForRefuseDocumentSuccess"
        @dialog-for-refuse-document:close="handleDialogForRefuseDocumentClose"
      />
    </div>
  </div>
</template>

<script>
import languageMessages from '@/mixins/languageMessages';
import Button from 'primevue/button';
import { Search, X, MoreVertical } from 'lucide-vue';
import { store } from '@/store';
import GlobalMixin from '@/mixins/global.mixin';
import moment from 'moment';
import * as icons from 'lucide-vue';
import { getFileExtension, getFileSrc } from '@/shared/helpers';
import { getDocument } from '@/modules/document-package/cruds/document.crud';
import { EXT_TO_ACCEPT, DOCUMENT_VALIDATION_STEP, DOCUMENT_DELIVERY_STATUS, DOCUMENTS_TYPE } from '@/shared/constants';
import _ from 'lodash';
import { mapActions } from 'vuex';
import DialogForRefuseDocument from '@/modules/onboarding/components/DialogForRefuseDocument';

export default {
  name: 'ListMyDocuments',
  components: {
    Search,
    X,
    MoreVertical,
    Button,
    DialogForRefuseDocument
  },
  mixins: [languageMessages, GlobalMixin],
  props: {
    documents: { type: Array, default: () => [] }
  },
  data() {
    return {
      filter: '',
      docs: [],
      originalDocs: [],
      docTypeSelected: null,
      showDialogDetail: false,
      documentSelected: {},
      showDialogRefuseDocument: false,
      DOCUMENT_VALIDATION_STEP,
      DOCUMENT_DELIVERY_STATUS,
      DOCUMENTS_TYPE
    };
  },
  computed: {
    docTypes() {
      return this.FormMenu(1011);
    }
  },
  watch: {
    documents: {
      handler(val) {
        this.docs = [];
        setTimeout(() => {
          this.docs = _.cloneDeep(val);
          this.originalDocs = _.cloneDeep(val);
        }, 250);
      },
      immediate: true,
      deep: true
    }
  },
  created() {
    moment.locale(this.lang);
  },
  methods: {
    ...mapActions({
      getNotifications: 'header/getNotifications'
    }),
    handleClickSign(document) {
      this.$emit('list-my-document:sign', document);
    },
    handleInputFilter(value) {
      let filter = value.trim().toLowerCase();
      if (filter === '') this.handleChangeDocType(this.docTypeSelected);
      let arr = this.docs.filter((doc) => doc.name.toLowerCase().indexOf(filter) > -1);
      this.docs = arr;
    },
    async viewDocument(file) {
      await getDocument(file.id, { showOnlyMyDocument: true }, store.userID(), file.deliveryContractId).then((record) => {
        let fileList = [];
        if (record.type === 11) {
          // store.state.myProfile.id
          fileList = record.childDocList.filter((list) => list.userId === null || parseInt(list.userId, 10) === 0);
          fileList = fileList.map((doc) => {
            const src = getFileSrc(doc, { contractId: doc.deliveryContractId });
            return {
              ...doc,
              fileName: this.replaceToRightFileNameExt(doc.fileName, doc.docSignedXid),
              src: `${src}?deliveryParentId=${doc.firstDeliveryParentId}`,
              thumb: process.env.VUE_APP_PATH_IMG_THUMB_URL + this.getRightXidDocument(doc.xid, doc.docSignedXid),
              status: EXT_TO_ACCEPT.includes(getFileExtension(this.getRightXidDocument(doc.xid, doc.docSignedXid)))
                ? this.manageStatusInAdvance(doc.status)
                : doc.status,
              ext: getFileExtension(this.getRightXidDocument(doc.xid, doc.docSignedXid)),
              isHovered: false
            };
          });
        } else {
          const src = getFileSrc(record, {
            imageOnly: false,
            contractId: record.deliveryContractId
          });
          fileList.push({
            ...record,
            fileName: this.replaceToRightFileNameExt(record.fileName, record.docSignedXid),
            type: record.type,
            xid: this.getRightXidDocument(record.xid, record.docSignedXid),
            status: EXT_TO_ACCEPT.includes(getFileExtension(this.getRightXidDocument(record.xid, record.docSignedXid)))
              ? this.manageStatusInAdvance(record.status)
              : record.status,
            toSign: record.requireManagerApproval === true && record.toSign === true,
            src: `${src}?deliveryParentId=${record.firstDeliveryParentId}&onlyViewDocument=false`,
            thumb: process.env.VUE_APP_PATH_IMG_THUMB_URL + this.getRightXidDocument(record.xid, record.docSignedXid),
            ext: getFileExtension(this.getRightXidDocument(record.xid, record.docSignedXid)),
            isHovered: false
          });
        }
        this.$previewImages({
          name: 'mydocuments-previewer-elem',
          images: fileList,
          focusIndex: 0,
          options: {
            presentationMode: 'doc',
            showStatusFileTag: true,
            hideCommentButton: false,
            hideDisLikeButton: false,
            hideLikeDislike: false,
            hideCropButton: true,
            hideDeleteButton: true,
            showSignFileButton: true
          }
        });
      });
    },
    handleChangeDocType(payload) {
      this.clearFilter();
      if (payload === null) {
        this.docs = this.originalDocs;
      } else {
        this.docs = this.originalDocs.filter((oD) => parseInt(oD.type) === parseInt(payload));
      }
    },
    getPublishedOn(value) {
      return moment(value).utc().format('DD MMMM YYYY');
    },
    getDocType(value) {
      const findIndex = this.docTypes.findIndex((d) => parseInt(d.value) === parseInt(value));
      if (findIndex !== -1) {
        return this.docTypes[findIndex].text;
      }
      return '';
    },
    clearFilter() {
      if (this.filter !== '') {
        this.filter = '';
        this.handleInputFilter('');
      }
    },
    validatedStatus(doc) {
      /**
       * Simplify status of flow user as
       * {
       *   2: RECEIVED
       * 	 3: READ,
       * }
       * to => TO SIGN
       * {
       *   5: VALIDATED
       * }
       * to => COMPLETED
       */

      let text = '';

      if (doc.requireManagerApproval === true) {
        let index = 0;

        if (doc.status === DOCUMENT_DELIVERY_STATUS.REFUSED || doc.senderStatus === DOCUMENT_DELIVERY_STATUS.REFUSED) {
          index = 3;
        } else if (doc.status === DOCUMENT_DELIVERY_STATUS.RECEIVED || doc.status === DOCUMENT_DELIVERY_STATUS.READ) {
          index = 0;
        } else if (doc.status === DOCUMENT_DELIVERY_STATUS.SIGNED) {
          index = 1;
        } else if (doc.status === DOCUMENT_DELIVERY_STATUS.VALIDATED) {
          index = 2;
        }

        const newSignatureStatuses = [
          this.FormMSG(15451212, 'To sign'),
          this.FormMSG(15451214, 'Signed'),
          this.FormMSG(15451215, 'Completed'),
          this.FormMSG(15451213, 'Refused')
        ];

        text = newSignatureStatuses[index];
      } else {
        const statuses = this.FormMenu(1012);
        const status = statuses.filter((status) => status.value === doc.status);

        text = status.length > 0 ? status[0].text : '';
      }

      return text;
    },
    getLucideIcon(name) {
      return icons[name];
    },
    viewDocumentDetail(item) {
      const documentLink = `/mydocuments/document-details/${item.id}/${item.deliveryContractId}`;
      // console.log("starting:", documentLink);
      this.$router.push({
        path: documentLink
      });
    },
    dateToString(date) {
      return new moment(date).utc().format('DD/MM/YYYY');
    },
    handleClickRefuse(item) {
      this.showDialogRefuseDocument = true;
      this.documentSelected = { ...item, docId: +item.id, currentStep: item.deliveryCurrentStep, documentDeliveryId: item.firstDeliveryId };
      // console.log({ documentSelected: this.documentSelected });
    },
    async handleDialogForRefuseDocumentSuccess(payload) {
      this.$emit('list:my-document:refused');
    },
    handleDialogForRefuseDocumentClose() {
      this.showDialogRefuseDocument = false;
      this.documentSelected = {};
    }
  }
};
</script>
